<template>
  <div class="flex flex-col">
    <!-- Título fora do gap-2: mb-2 + o mt-4 do primeiro ComboBoxWithOther = 24px, como nas demais etapas. -->
    <ScheduleStepHeading :title="SCHEDULE_SECTION_LABELS.requirements" class="mb-2" />

    <div class="flex flex-col gap-2">
      <ComboBoxWithOther
        v-model="step2.positions"
        :items="positions"
        label="Selecione as posições da escala"
        :error="!!props.errors.positions"
        :error-message="props.errors.positions"
        @update:modelValue="props.onClearError('positions')"
      />

      <ComboBoxWithOther
        v-model="step2.shifts"
        :items="shifts"
        label="Selecione os turnos da escala"
        :error="!!props.errors.shifts"
        :error-message="props.errors.shifts"
        @update:modelValue="props.onClearError('shifts')"
      />

      <!-- Bloco próprio para o toggle não parecer parte do campo de turnos acima. -->
      <section
        class="mt-4 rounded-lg border border-lightGray30 p-4 text-left"
        data-testid="schedule-requirements-formation"
      >
        <DsSwitch
          v-model="step2.formationRequired"
          data-testid="schedule-requirements-formation-switch"
        >
          <span class="font-bold">Exigir nível de formação na escala</span>
        </DsSwitch>
        <p class="mt-1 text-sm text-[#68688D]">
          Ao ativar, as regras da política de pagamento podem variar conforme a formação do médico.
        </p>

        <ComboBoxWithOther
          v-if="step2.formationRequired"
          v-model="step2.formations"
          :items="formations"
          label="Selecione os níveis de formação da escala"
          :error="!!props.errors.formations"
          :error-message="props.errors.formations"
          @update:modelValue="props.onClearError('formations')"
        />
      </section>

      <div
        v-if="flags.enabledProfessionalDocuments"
        class="mt-6 border-t border-gray-200 pt-6"
        data-testid="schedule-requirements-qualification"
      >
        <QualificationRequirementSection
          v-model="stepQualificationRequirement.policy"
          @validity-change="onQualificationValidity"
        />
        <p
          v-if="props.errors.qualificationPolicy"
          class="mt-2 text-sm text-negative text-left"
          data-testid="schedule-requirements-qualification-error"
        >
          {{ props.errors.qualificationPolicy }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, inject } from 'vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import { useCreateScheduleStore } from '@/pinia-store/useCreateScheduleStore'
import { storeToRefs } from 'pinia'
import ComboBoxWithOther from './ComboBoxWithOther.vue'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import QualificationRequirementSection from '@/components/QualificationRequirement/QualificationRequirementSection.vue'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useErrorToast } from '@/composables/useErrorToast'
import { SCHEDULE_SECTION_LABELS } from '@/constants/schedule-sections.constants'
import ScheduleStepHeading from './ScheduleStepHeading.vue'

const store = (inject('createScheduleStore') ?? useCreateScheduleStore()) as ReturnType<
  typeof useCreateScheduleStore
>
const { step2, stepQualificationRequirement } = storeToRefs(store)
const { flags } = useFeatureFlags()

type Item = { title: string; value: number }

const positions = ref<Item[]>([])
const formations = ref<Item[]>([])
const shifts = ref<Item[]>([])

const fieldService = new FieldOptionsService()
const { notifyError } = useErrorToast()

const fetchFieldOptions = async () => {
  try {
    const [positionResponse, shiftsResponse, formationResponse] = await Promise.all([
      fieldService.getPositions(),
      fieldService.getShifts(),
      fieldService.getFormations()
    ])

    positions.value = positionResponse.map((position) => ({
      title: position.name,
      value: position.id
    }))

    formations.value = formationResponse.map((formation) => ({
      title: formation.name,
      value: formation.id
    }))

    shifts.value = shiftsResponse.map((shift) => ({
      title: shift.name,
      value: shift.id
    }))
  } catch (error) {
    notifyError(error, 'Não foi possível carregar as posições, turnos e formações.')
  }
}

onMounted(fetchFieldOptions)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<{
  errors: Record<string, string>
  onClearError: (field: string) => void
}>()

/** A validade da Qualificação exigida trava o avanço do passo (CreateSchedule). */
function onQualificationValidity(valid: boolean) {
  stepQualificationRequirement.value.isValid = valid
  if (valid) props.onClearError('qualificationPolicy')
}
</script>
